import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAudio } from '../../hooks/useAudio';
import JobCard from '../../components/JobCard';
import Spinner from '../../components/Spinner';
import useResponsive from '../../hooks/useResponsive';
import { getWithExpiry } from '../../utils/sessionStorage';
import { saveInterview } from '../../api/ApplicationService';
import { transcribeVideoOrAudio } from '../../utils/transcriptionUtils';
import { isUserSafari } from '../../utils/safariUtils';
import ToastMessage, { ToastMessageProps } from '../../components/ToastMessage';
import { useInternetConnection } from '../../utils/connection';
import { S3UploadMetadata } from '../../types/file';
/*
  Audio Interview
 */
const AudioInterview = () => {
  const navigate = useNavigate();
  const isMobile = useResponsive('md');
  const isSafari = isUserSafari();
  const { 
    isAudioActive, 
    audioLevel, 
    startAudio,
    stopAudio, 
    startStreamedAudioRecording, 
    stopStreamedAudioRecording,
    recordedAudioUrl: hookRecordedAudioUrl,
    handleSubmitStreamRecording,
    recordingError,
    isRecording: isHookRecording,
    setIsRecording: setIsHookRecording,
    audioSilenceError
  } = useAudio();
  
  const [isLoading, setIsLoading] = useState(true);
  // const [isHookRecording, setIsHookRecording] = useState(false);
  const [hasRecorded, setHasRecorded] = useState(false);
  const [remainingTries, setRemainingTries] = useState(3);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState<string | null>(hookRecordedAudioUrl);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [job, setJob] = useState<any>(null);
  const [startTime, setStartTime] = useState<number | null>(null);
  const hasStoppedRef = useRef(false); // Prevent multiple stops
  const [recordingTime, setRecordingTime] = useState(0);
  const [isReRecording, setIsReRecording] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  // blobs
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null);

  const [audioLinks, setAudioLinks] = useState<Record<string, string>>({});

  // asnyc loading state for buttons
  const [processingButtons, setProcessingButtons] = useState<{[key: string]: boolean}>({
    start: false,
    stop: false,
    reRecord: false,
    submit: false,
  });

  const handleButtonClick = async (buttonId: string, action: () => Promise<void>) => {
    const startTime = new Date();
    try {
      console.log(buttonId, ' Starting button action', new Date().toISOString());
      setProcessingButtons(prev => ({ ...prev, [buttonId]: true }));
      console.log(buttonId, ' Button action started', new Date().toISOString());
      await action();
      console.log(buttonId, ' Button action completed', new Date().toISOString());
    } finally {
      console.log(buttonId, ' Button action finally', new Date().toISOString());
      console.log("Time taken:", new Date().getTime() - startTime.getTime(), "ms");
      setProcessingButtons(prev => ({ ...prev, [buttonId]: false }));
    }
  };


  const applicantGuid = getWithExpiry('applicantGuid') || undefined;
  const jobId = getWithExpiry('jobId') || undefined;
  const interviewId = getWithExpiry('interviewId') || undefined;
  const companyId = getWithExpiry('companyId') || undefined;

  // s3
  const s3MetaData: S3UploadMetadata = {
    interviewId: interviewId || '',
    dateInterviewed: new Date().toISOString(),
    companyId: companyId || '',
  };

  // Audio refs
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);

  // tts
  const ttsAudio = getWithExpiry('ttsAudio') || undefined;
  const isTTSPlayedRef = useRef(false);
  const questionAudioRef = useRef<HTMLAudioElement | null>(null);

  
  // Questions
  const questionnaires = getWithExpiry('questionnaires');
  const totalQuestions = questionnaires.length;
  const currentQuestion = questionnaires[currentQuestionIndex];
  const [currentRecordingQuestion, setCurrentRecordingQuestion] = useState<number | null>(null);

  // errors
  const [showToast, setShowToast] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastMessageProps>({
    hasTitle: true,
    title: '',
    message: '',
    type: 'info',
    isMobile: isMobile,
  });

  // recording error
  useEffect(() => {
    if (recordingError) {
      console.log(recordingError)
      setToast({
        hasTitle: true,
        title: 'Device Error',
        message: recordingError.replace(/\(\d+\)$/, ''),
        type: 'error',
        isMobile: isMobile,
        isOpen: showToast,
      });
      setShowToast(true);
    }
    else{
      setShowToast(false);
      setError(null);
    }
  }, [recordingError]);

  // audio recording error handling
  useEffect(() => {
    if(audioSilenceError){
      setToast({
        hasTitle: true,
        title: 'Are you talking?',
        message: audioSilenceError.replace(/\(\d+\)$/, ''),
        type: 'error',
        isMobile: isMobile,
        isOpen: showToast,
      });
      setShowToast(true);
    }else{
      setShowToast(false);
    }
  }, [audioSilenceError]);

  // general error
  useEffect(() => {
    if (error) {
      setToast({
        hasTitle: true,
        title: 'Error',
        message: error.replace(/\(\d+\)$/, ''),
        type: 'error',
        isMobile: isMobile,
        isOpen: showToast,
      });
      setShowToast(true);
    }
    else{
      setShowToast(false);
      setError(null);
    }
  }, [error]);

  //internet connection
  const isConnected = useInternetConnection();

  useEffect(() => {
    if (!isConnected) {
      const connectionMessage = `You are offline. Please reconnect to continue.(${Date.now()})`;
      setToast({
        hasTitle: true,
        title: 'Connection Lost',
        message: connectionMessage.replace(/\(\d+\)$/, ''),
        type: 'error',
        isMobile: isMobile,
        isOpen: true,
      });
      setShowToast(true);
    } else {
      setShowToast(false);
      setError(null);
    }
  }, [isConnected]);


  // Load job info
  useEffect(() => {
    setJob(getWithExpiry('job'));
    setIsLoading(false);
  }, [jobId, navigate]);

  useEffect(() => {
    setRecordedAudioUrl(hookRecordedAudioUrl);
  }, [hookRecordedAudioUrl]);
  
  useEffect(() => {
    isTTSPlayedRef.current = false;
  }, [currentQuestionIndex]);

  // Add this useEffect to play TTS audio when question changes
  useEffect(() => {
    if(isTTSPlayedRef.current) return;
    // Clean up previous audio if exists
    if (questionAudioRef.current) {
      questionAudioRef.current.pause();
      questionAudioRef.current.src = '';
    }
    
    if (ttsAudio && Array.isArray(ttsAudio) && ttsAudio.length > currentQuestionIndex) {
      // Create a URL that the browser can access by converting file paths to API endpoints
      const audioPath = ttsAudio[currentQuestionIndex];
      // Extract just the filename from the path
      const filename = audioPath.split('/').pop() || audioPath.split('\\').pop();
      
      // Create a proper audio URL - this must match your server route exactly
      const audioUrl = `/api/v1/media/audio/stream/${encodeURIComponent(filename)}`;
      
      console.log(`Playing TTS audio for question ${currentQuestionIndex} from: ${audioUrl}`);
      
      // Create a new audio element with the HTTP URL
      const audioElement = new Audio(audioUrl);
      questionAudioRef.current = audioElement;
      
      // Play the audio automatically
      audioElement.play().catch(error => {
        console.error('Failed to play TTS audio:', error);
      });
      
      // Add event listener for debugging
      audioElement.addEventListener('ended', () => {
        console.log('TTS audio playback finished');
      });
    }
    else {
      console.log("No TTS audio found or invalid index");
    }
    
    // Cleanup function
    return () => {
      if (questionAudioRef.current) {
        questionAudioRef.current.pause();
        questionAudioRef.current.src = '';
      }
    };
  }, [currentQuestionIndex]);

  // Handle timer
  useEffect(() => {
    if (isHookRecording && !startTime) {
      const now = Date.now();
      setStartTime(now);
      hasStoppedRef.current = false;
      
      timerIntervalRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - now) / 1000);
        setRecordingTime(elapsed);
        
        // Hard stop at 120 seconds
        if (elapsed >= 120 && !hasStoppedRef.current) {
          hasStoppedRef.current = true;
          console.log('Timer auto-stop triggered at:', elapsed, 'seconds');
          
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
          }
          
          // Force stop recording immediately
          stopRecording();
        }
      }, 1000);
    } else if (!isHookRecording) {
      // Reset when not recording
      setStartTime(null);
      hasStoppedRef.current = false;
      
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }
    
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [isHookRecording]);

  // Start recording function
  const startRecording = async (isRerecord = false) => {
    try {
      if(!isConnected){
        setToast({
          hasTitle: true,
          title: 'Connection Lost',
          message: `You are offline. Please reconnect to continue.(${Date.now()})`,
          type: 'error',
          isMobile: isMobile,
          isOpen: true,
        });
        setShowToast(true);
        return;
      }

      if(isRerecord){
        setIsReRecording(true);
        setRecordedAudioUrl(null);
      }
      // await new Promise(resolve => setTimeout(resolve, 2000));
      // mute when forced to record
      if (questionAudioRef.current) {
        questionAudioRef.current.pause();
        questionAudioRef.current.src = '';
      }
      await startAudio();
      chunksRef.current = [];
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      
      // Set which question we're recording for
      setCurrentRecordingQuestion(currentQuestionIndex);
      
      // Start the streamed recording
      await startStreamedAudioRecording(applicantGuid, currentQuestionIndex, currentQuestion.questionText, currentQuestionIndex === totalQuestions - 1, totalQuestions, s3MetaData);
      
      setIsHookRecording(true);
      setRecordingTime(0);
      
      // Start recording timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      if(isRerecord){
        setIsReRecording(false);
      }
    } catch (error) {
      console.error('Error starting recording:', error);
      setError(`Recording failed. Please check your microphone permissions and try again. (${Date.now()})`);
      setIsHookRecording(false);
    }
  };
  
  // Stop recording function
  const stopRecording = async () => {
    // await new Promise(resolve => setTimeout(resolve, 2000));
    stopStreamedAudioRecording();
    stopAudio();
    setRecordingTime(0);
    
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
    }
    
    setIsHookRecording(false);
    setHasRecorded(true);
    setRemainingTries(prev => prev - 1);
    
    // Clear recording timer
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  };
  
  // Play the recorded audio
  const playRecording = () => {
    try{
      if (audioRef.current && recordedAudioUrl) {
        console.log("Playing from recordedAudioUrl:", recordedAudioUrl);
        audioRef.current.src = recordedAudioUrl;
        audioRef.current.muted = false;
        audioRef.current.volume = 1.0;
        audioRef.current.play().catch(e => {
          console.error('Audio playback error:', e);
          setError(`Unable to play audio. (${Date.now()})`);
        });
      } else {
        setError(`Unable to play audio. (${Date.now()})`);
      }
    }catch(error){
      console.error('Error playing recording:', error);
      setError(`Unable to play audio. (${Date.now()})`);
    }
  };

  const muteAudio = () => {
    try{
      const newMutedState = !isMuted;
      setIsMuted(newMutedState);
      
      if (audioRef.current && recordedAudioUrl) { 
        audioRef.current.muted = newMutedState;
        audioRef.current.play().catch(e => setError(`Unable to mute audio. (${Date.now()})`));
  
        // cleanup
        const onEnded = () => {
          audioRef.current?.removeEventListener('ended', onEnded);
        };
  
        audioRef.current.addEventListener('ended', onEnded);
      }
    }catch(error){
      console.error('Error muting audio:', error);
      setError(`Unable to mute audio. (${Date.now()})`);
    }
    
  };
  
  // Format time for display
  const formatTimeRemaining = () => {
    const secondsRemaining = 120 - recordingTime;
    const minutes = Math.floor(secondsRemaining / 60);
    const seconds = secondsRemaining % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };
  
  const reRecord = async () => {
    setIsReRecording(true);
    // await new Promise(resolve => setTimeout(resolve, 2000));
    if (remainingTries > 0) {
      
      // Reset UI state
      // setHasRecorded(false);
      setRecordedAudioUrl(null);
      
      // Start a new recording for this question
      startRecording();
      
      console.log(`Re-recording question ${currentQuestionIndex}`);
    }
    setIsReRecording(false);
  };
  
  // Navigate to next question or finish
  const goToNextQuestion = () => {
    if (currentQuestionIndex < questionnaires.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setHasRecorded(false);
      setRecordedAudioUrl(null);
      setRemainingTries(3);
    } else {
      // Last question - complete interview
      navigate('/interview/success');
    }
  };

  const submitStreamRecording = async () => {
    try{
      // mute playback audio
      if(audioRef.current){
        audioRef.current.pause();
        audioRef.current.src = '';
      }

      await handleSubmitStreamRecording();

      const questionId = `question_${currentQuestionIndex}`;
      const isLastQuestion = currentQuestionIndex === totalQuestions - 1;
      
      // Store local recording for replay
      if (recordedBlob && recordedUrl) {
        setAudioLinks(prev => ({ ...prev, [questionId]: recordedUrl }));
      }
      
      // Navigate or finalize
      if (!isLastQuestion) {
        setHasRecorded(false);
        setRemainingTries(3);
        setCurrentQuestionIndex(prev => prev + 1);
      }else{
        navigate('/interview/success');
      }
    }catch(error){
      console.error('Error submitting recording:', error);
      setError(`Failed to submit recording. Please check your microphone or internet connection and try again. (${Date.now()})`);
      throw error;
    }
    
  };
  
  // Submit recording
  const submitRecording = async () => {
    try{
      if(!isConnected){
        setToast({
          hasTitle: true,
          title: 'Connection Lost',
          message: `You are offline. Please reconnect to continue.(${Date.now()})`,
          type: 'error',
          isMobile: isMobile,
          isOpen: true,
        });
        setShowToast(true);
        return;
      }
      // await new Promise(resolve => setTimeout(resolve, 2000));
      if (!hasRecorded) {
        console.warn('Cannot submit: No recording available');
        return;
      }
      
      // Call the stream submission handler
      await submitStreamRecording();
    }catch(error){
      console.error('Error submitting recording:', error);
      setError(`Failed to submit recording. Please check your microphone or internet connection and try again. (${Date.now()})`);
    }
    
  };

  // for cleanup
  useEffect(() => {
    return () => {
      // Stop recording if it's still going
      if (mediaRecorderRef.current && isHookRecording) {
        mediaRecorderRef.current.stop();
      }
      
      // Stop and clean up audio stream
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
      }
      
      // Clear recording timer
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      
      // Release any created object URLs
      if (recordedUrl) {
        URL.revokeObjectURL(recordedUrl);
      }
      if (recordedAudioUrl && recordedAudioUrl !== recordedUrl) {
        URL.revokeObjectURL(recordedAudioUrl);
      }
    };
  }, [isHookRecording, recordedUrl, recordedAudioUrl, hookRecordedAudioUrl]);

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        padding: isMobile ? '1rem' : '2rem',
        paddingTop: '0',
      }}
    >
      {toast && (
        <ToastMessage
          {...toast}
          isOpen={showToast}
          onClose={() => setShowToast(false)}
        />
      )}
      <div
        style={{
          width: isMobile ? '100%' : '70%',
          textAlign: 'left',
          padding: '0',
          borderRadius: '8px',
          marginTop: '0',
        }}
      >
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Spinner />
          </div>
        ) : job ? (
          <JobCard job={job} isMobile={isMobile} />
        ) : (
          <div>No job information available</div>
        )}

        {/* Main content area - two columns on desktop, stacked on mobile */}
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Spinner />
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '2rem',
              minHeight: 'calc(100vh - 400px)', 
            }}
          >
            {/* Left Column - Question */}
            <div
              style={{
                flex: isMobile ? '1' : '1',
              }}
            >
              {/* Question Counter */}
              <div style={{ marginBottom: '1rem' }}>
                <p style={{ color: '#555', fontWeight: '500' }}>
                  Question {currentQuestionIndex + 1} of {totalQuestions}
                </p>
              </div>

              {/* Question Text with Back Button */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.75rem',
                  marginBottom: isMobile ? '1.2rem' : '1.5rem',
                }}
              >
                <h2
                  style={{
                    fontSize: '1.25rem',
                    fontWeight: '500',
                    color: '#333',
                    margin: '0',
                  }}
                >
                  "{currentQuestion.questionText}"
                </h2>
              </div>
              {/* <button
                onClick={goToPreviousQuestion}
                disabled={currentQuestionIndex === 0}
                style={{
                  padding: '0.25rem 0.5rem',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: currentQuestionIndex === 0 ? 'not-allowed' : 'pointer',
                  opacity: currentQuestionIndex === 0 ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img
                  src="/svgs/back.svg"
                  alt="Back"
                  style={{
                    width: '1.2rem',
                    height: '1.2rem',
                  }}
                />
              </button> */}
            </div>

            {/* Right Column - Audio Waveform and Controls */}
            <div style={{ flex: isMobile ? '1' : '1' }}>
              {/* Audio Waveform with Border */}
              <div
                style={{
                  width: '100%',
                  aspectRatio: '4/3',
                  position: 'relative',
                  borderRadius: '8px',
                  border: '1px solid #e0e4e8',
                  marginBottom: '1rem',
                  overflow: 'hidden',
                  backgroundColor: '#f8f9fa',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {/* Audio playback element */}
                <audio ref={audioRef} src={recordedAudioUrl || ''} style={{ display: 'none' }} />

                {isHookRecording ? (
                  <div style={{ 
                    width: '100%', 
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center', 
                    alignItems: 'center',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    padding: '15px',
                    marginTop: '5.0rem',
                    gap: isMobile ? '3.0rem' : '5.0rem',
                    maxHeight: '350px',  // Control max height
                  }}>
                    <AudioMic level={audioLevel} isRecording={isHookRecording} />
                    <AudioWaveform level={audioLevel} isMobile={isMobile} />
                  </div>
                ) : hasRecorded && !isHookRecording ? (
                  <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                    <div style={{ 
                      width: '80%', 
                      height: '50%',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)'
                    }}>
                      <img src="/svgs/waveform-gray.svg" alt="Audio waveform" style={{ width: '100%', height: '100%' }} />
                    </div>
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                        height: '70%',
                        background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 40%, rgba(0,0,0,0) 100%)',
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'space-between',
                        padding: '0 20px 20px 20px',
                      }}
                    >
                      {/* Play buttons after recording */}
                      <>
                        <button
                          onClick={playRecording}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'white',
                            fontSize: '16px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <img
                            src="/svgs/play-button.svg"
                            alt="Play"
                            style={{ width: '16px', height: '16px' }}
                          />
                        </button>

                        <button
                          onClick={muteAudio}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'white',
                            fontSize: '16px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <img
                            src="/svgs/audio-replay.svg"
                            alt="Audio"
                            style={{ width: '16px', height: '16px' }}
                          />
                        </button>
                      </>
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center' }}>
                    <img src="/svgs/mic.svg" alt="Microphone" style={{ width: '64px', height: '64px', opacity: 0.5 }} />
                    <p style={{ color: '#6c757d', marginTop: '1rem', fontSize: isMobile ? '0.8rem' : '0.9rem' }}>Click "Start Recording" to begin</p>
                  </div>
                )}
              </div>

              {/* Recording Controls */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '1rem',
                  marginBottom: '1rem',
                }}
              >
                {/* Show different controls based on recording state */}
                {!isHookRecording && !hasRecorded && !isReRecording ? (
                  <>
                    <button
                      onClick={() => handleButtonClick('start', async () => {
                        await startRecording();
                      })}
                      style={{
                        padding: isMobile ? '0.4rem 0.6rem' : '0.5rem 1.5rem',
                        backgroundColor: '#0D6EFD',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontWeight: '500',
                        fontSize: isMobile ? '0.8rem' : '0.9rem',
                        cursor: 'pointer',
                      }}
                    >
                      {processingButtons.start ? <Spinner size="sm" color="light" /> : 'Start Recording'}
                    </button>

                    <div
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#e9ecef',
                        borderRadius: '16px',
                        fontSize: '14px',
                        color: '#6c757d',
                      }}
                    >
                      {remainingTries} tries left
                    </div>
                  </>
                ) : isHookRecording ? (
                  <>
                    <button
                      onClick={() => handleButtonClick('stop', async () => {
                        await stopRecording();
                      })}
                      style={{
                        padding: isMobile ? '0.4rem 0.6rem' : '0.5rem 1.5rem',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontWeight: '500',
                        fontSize: isMobile ? '0.8rem' : '0.9rem',
                        cursor: 'pointer',
                      }}
                    >
                      {processingButtons.stop ? <Spinner size="sm" color="light" /> : 'Stop Recording'}
                    </button>
                    {
                      recordingTime >= 90 && (
                        <span
                          style={{
                            padding: '0.2rem 0.8rem',
                            borderRadius: '10px',
                            fontWeight: '500',
                            fontSize: '0.85rem',
                            color: '#6c757d',
                          }}
                        >
                          {formatTimeRemaining()}
                        </span>
                      )
                    }

                    <div
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#e9ecef',
                        borderRadius: '16px',
                        fontSize: '0.85rem',
                        color: '#6c757d',
                      }}
                    >
                      {remainingTries} tries left
                    </div>
                  </>
                ) : isMobile ? (
                  /* Mobile layout - stacked */
                  <>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        gap: '0.75rem',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          width: '100%',
                        }}
                      >
                        <button
                          onClick={() => handleButtonClick('reRecord', async () => {
                            // await reRecord();
                            await startRecording(true);
                          })}
                          disabled={remainingTries === 0}
                          style={{
                            flex: '1',
                            marginRight: '0.5rem',
                            padding: '0.5rem 1rem',
                            backgroundColor: 'white',
                            color: '#333',
                            border: '1px solid #ced4da',
                            borderRadius: '4px',
                            fontWeight: '500',
                            cursor: remainingTries === 0 ? 'not-allowed' : 'pointer',
                            opacity: remainingTries === 0 ? 0.5 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            justifyContent: 'center',
                          }}
                        >
                          {processingButtons.reRecord ? <Spinner size="sm" color="primary" /> : <><img src="/svgs/retry.svg" alt="Rerecord" /> Rerecord</>}
                        </button>

                        <button
                          onClick={() => handleButtonClick('submit', async () => {
                            await submitRecording();
                          })}
                          style={{
                            flex: '1',
                            marginLeft: '0.5rem',
                            padding: '0.5rem 1rem',
                            backgroundColor: '#11807a',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            display: 'flex',
                            gap: '0.5rem',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {processingButtons.submit ? <Spinner size="sm" color="light" /> : <><img src="/svgs/paperplane.svg" alt="Submit" /> Submit</>}
                        </button>
                      </div>

                      <div
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: '#e9ecef',
                          borderRadius: '16px',
                          fontSize: '0.85rem',
                          color: '#6c757d',
                          textAlign: 'center',
                          width: 'fit-content',
                          margin: '0 auto',
                        }}
                      >
                        {remainingTries} tries left
                      </div>
                    </div>
                  </>
                ) : (
                  /* Desktop layout - side by side with tries left in middle */
                  <>
                    <div
                      style={{ display: 'flex', gap: '1rem', alignItems: 'center', width: '100%' }}
                    >
                      <button
                        onClick={() => handleButtonClick('reRecord', async () => {
                          // await reRecord();
                          await startRecording(true);
                        })}
                        disabled={remainingTries === 0}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: 'white',
                          color: '#333',
                          border: '1px solid #ced4da',
                          borderRadius: '4px',
                          fontWeight: '500',
                          gap: '0.5rem',
                          cursor: remainingTries === 0 ? 'not-allowed' : 'pointer',
                          opacity: remainingTries === 0 ? 0.5 : 1,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {processingButtons.reRecord ? <Spinner size="sm" color="primary" /> : <><img src="/svgs/retry.svg" alt="Rerecord" /> Rerecord</>}
                      </button>

                      <div
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: '#e9ecef',
                          borderRadius: '16px',
                          fontSize: '0.85rem',
                          color: '#6c757d',
                          margin: '0 auto',
                        }}
                      >
                        {remainingTries} tries left
                      </div>

                      <button
                        onClick={() => handleButtonClick('submit', async () => {
                          await submitRecording();
                        })}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: '#11807a',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                        }}
                      >
                        {processingButtons.submit ? <Spinner size="sm" color="light" /> : <><img src="/svgs/paperplane.svg" alt="Submit" /> Submit</>}
                      </button>
                    </div>
                  </>
                )}
              </div>

              {/* Support Message */}
              <div style={{
                textAlign: 'center',
                marginBottom: '1rem',
                marginTop: isMobile ? '1.5rem' : '1.8rem',
                fontSize: isMobile ? '0.8rem' : '0.9rem',
                color: '#6c757d',
              }}>
                Need Assistance? Please email us at <a href="mailto:jobseeker-support@jobtarget.com" style={{ color: '#6c757d', textDecoration: 'underline' }}>JobSeeker Support</a>.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Audio Waveform Component - reduced height and padding
const AudioWaveform = ({ level, isMobile }: { level: number, isMobile: boolean }) => {
  // Convert 0-255 audio level to 0-100 percentage
  const levelPercentage = Math.min(100, Math.max(0, level * 3.0 * 100 / 255));
  
  return (
    <div className="audio-level-container" style={{
      width: '100%',
      height: 'auto',
      backgroundColor: '#f8f9fa',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '10px',
      marginBottom: '20px' 
    }}>
      {/* Wrapper to ensure proper sizing */}
      <div style={{
        position: 'relative',
        width: isMobile ? '285px' : '391px',
        height: '45px'
      }}>
        {/* Background waveform (gray) */}
        <div style={{ position: 'absolute', top: 0, left: 0, width: isMobile ? '285px' : '391px', height: '45px' }}>
          <img src="/svgs/waveform-gray.svg" alt="Audio waveform" style={{ width: isMobile ? '285px' : '391px', height: '45px' }} />
        </div>
        
        {/* Active blue part */}
        <div style={{ 
          position: 'absolute', 
          top: 0,
          left: 0,
          height: '45px', 
          width: `${levelPercentage}%`, 
          overflow: 'hidden' 
        }}>
          <img src="/svgs/waveform-blue.svg" alt="" style={{ width: isMobile ? '285px' : '391px', height: '45px' }} />
        </div>
      </div>
    </div>
  );
};

// AudioMic component - reduced size for better fit
const AudioMic = ({ level, isRecording }: { level: number, isRecording: boolean }) => {
  // Convert 0-255 audio level to 0-100 percentage
  const levelPercentage = Math.min(100, Math.max(0, level * 3.0 * 100 / 255));
  
  // Calculate animation values based on audio level
  const pulseScale = 1 + (levelPercentage / 100) * 0.3; // Scale between 1.0 and 1.3
  
  return (
    <div style={{
      width: '100%',
      height: 'auto',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'relative',
      margin: '20px auto' // Reduced margin
    }}>
      {/* Outer pulsing circle - reduced size */}
      <div style={{
        width: '120px',
        height: '120px',
        borderRadius: '50%',
        backgroundColor: '#cbddee',
        position: 'absolute',
        transform: isRecording ? `scale(${pulseScale})` : 'scale(1)',
        transition: 'transform 0.05s linear, background-color 0.1s ease-out',
        opacity: 0.7
      }} />
      
      {/* Inner static circle - reduced size */}
      <div style={{
        width: '90px',
        height: '90px', 
        borderRadius: '50%',
        backgroundColor: '#eaf3fc',
        position: 'absolute',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1
      }}>
        {/* Microphone icon */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <img src="/svgs/micbold.svg" alt="Microphone" style={{ width: '48px', height: '48px', fontWeight: 'bold' }} />
        </div>
      </div>
      
      {/* Active outer circle overlay - only visible when recording */}
      {isRecording && levelPercentage > 5 && (
        <div style={{
          width: '120px', // Smaller size
          height: '120px', // Smaller size
          borderRadius: '50%',
          backgroundColor: '#2f86dd',
          position: 'absolute',
          transform: `scale(${pulseScale})`,
          transition: 'transform 0.2s ease',
          opacity: levelPercentage / 200 + 0.3, // Vary opacity with level
          zIndex: 0
        }} />
      )}
    </div>
  );
};

export default AudioInterview;
